import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme, Theme } from '../contexts/ThemeContext';
import CloseIcon from './icons/CloseIcon';

// Same card, header and footer as TimeSignatureEditorModal
const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    overflowY: 'auto' as const,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
    zIndex: 50,
    transition: `opacity ${theme.motion.duration.normal}ms ${theme.motion.easing.standard}`,
    backgroundColor: theme.colors.modalOverlay,
    opacity: 1,
    pointerEvents: 'auto' as const,
  },
  overlayHidden: {
    backgroundColor: 'transparent',
    opacity: 0,
    pointerEvents: 'none' as const,
  },
  modalContent: {
    backgroundColor: theme.colors.modalBackground,
    borderRadius: theme.radii.xl,
    boxShadow: theme.shadows.modal,
    width: '100%',
    maxWidth: '32rem',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    transition: `all ${theme.motion.duration.normal}ms ${theme.motion.easing.standard}`,
    opacity: 1,
    transform: 'scale(1)',
  },
  modalContentHidden: {
    opacity: 0,
    transform: 'scale(0.95)',
  },
  header: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.xl,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  titleBlock: {
    minWidth: 0,
  },
  title: {
    fontSize: theme.typography.sizes.xxl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    margin: 0,
  },
  subtitle: {
    margin: 0,
    marginTop: theme.spacing.xxs,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  closeButton: {
    color: theme.colors.textSecondary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing.xl,
    borderTop: `1px solid ${theme.colors.border}`,
    gap: theme.spacing.lg,
  },
  footerStart: {
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  clearButton: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    backgroundColor: theme.colors.surfaceSecondary,
    borderRadius: theme.radii.md,
    border: 'none',
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    cursor: 'pointer',
  },
  applyButton: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.radii.md,
    border: 'none',
    color: theme.colors.textInverse,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    cursor: 'pointer',
  },
});

export interface SectionEditorModalFrameProps {
  isOpen: boolean;
  title: string;
  /** Usually the section's name, so the user knows which section they edit. */
  subtitle?: string;
  onClose: () => void;
  onApply: () => void;
  /** Shown only when there is a value to clear. */
  onClear?: () => void;
  /** Labels for the two footer buttons; the bar editor uses "Cancel" for the clear slot. */
  applyLabel?: string;
  clearLabel?: string;
  /** Rendered at the footer's left (the bar editor's in-card Undo). */
  footerStart?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared shell for the section repeat and note editors, laid out like
 * TimeSignatureEditorModal. Rendered into document.body, and clicks stop
 * here: the section's own click handler toggles it active, and React
 * events still bubble through a portal to their React ancestors.
 */
const SectionEditorModalFrame: React.FC<SectionEditorModalFrameProps> = ({
  isOpen,
  title,
  subtitle,
  onClose,
  onApply,
  onClear,
  applyLabel = 'Apply',
  clearLabel = 'Clear',
  footerStart,
  children,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    setShowContent(isOpen);
  }, [isOpen]);

  if (!isOpen && !showContent) {
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      style={{
        ...styles.overlay,
        ...(isOpen ? {} : styles.overlayHidden),
      }}
      onClick={handleOverlayClick}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          ...styles.modalContent,
          ...(showContent && isOpen ? {} : styles.modalContentHidden),
        }}
      >
        <div style={styles.header}>
          <div style={styles.titleBlock}>
            <h2 style={styles.title}>{title}</h2>
            {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} style={styles.closeButton} aria-label="Close modal">
            <CloseIcon size={20} color="currentColor" />
          </button>
        </div>

        {children}

        <div style={styles.footer}>
          {footerStart && <div style={styles.footerStart}>{footerStart}</div>}
          {onClear && (
            <button type="button" onClick={onClear} style={styles.clearButton}>
              {clearLabel}
            </button>
          )}
          <button type="button" onClick={onApply} style={styles.applyButton}>
            {applyLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SectionEditorModalFrame;
